import { TransactionStatusEnum } from './../../models/transaction-status.enum';
import { TransactionService } from './../../services/transactions/transaction.service';
import {
  ArrayUtilities,
  SortOrder,
} from './../../shared/utilities/array-utilities';
import { ITransaction } from './../../models/transaction';
import { SaleCampaignService } from './../../services/sale-campaign/sale-campaign.service';
import { StateContext } from '@ngxs/store';
import { SaleCampaign } from './sale-campaign.actions';
import { Product } from './../../models/product';
import { ISaleCampaign } from './../../models/sale-campaign';
import { Injectable, OnDestroy } from '@angular/core';
import { Action, Selector, State, Store } from '@ngxs/store';
import { SaleCampaignStateModel } from './sale-campaign.model';
import { debounce, debounceTime, takeUntil, tap } from 'rxjs/operators';
import { Subject, Observable, Subscription, of } from 'rxjs';
import { IProductGroup } from './../../models/product-group';
import { ProductGroupService } from '../../services/product/product-group.service';

@State<SaleCampaignStateModel>({
  name: 'SaleCampaign',
  defaults: {
    campaigns: undefined,
    orders: undefined,
    selectedCampaign: undefined,
    isLoading: false,
    products: undefined,
    updatedCampaignId: undefined,
    updatedOrderId: undefined,
    updatedProductGroupId: undefined,
    selectedProductGroup: undefined,
    productGroups: undefined,
  },
})
@Injectable()
export class SaleCampaignState implements OnDestroy {
  unSubscribeAll$ = new Subject<void>();
  constructor(
    private store: Store,
    private productGroupService: ProductGroupService,
    private campaignService: SaleCampaignService,
    private transactionService: TransactionService
  ) {}

  @Selector()
  static getSelectedCampaign(state: SaleCampaignStateModel): ISaleCampaign {
    return state.selectedCampaign;
  }

  @Selector()
  static getCampaignList(state: SaleCampaignStateModel): ISaleCampaign[] {
    return state.campaigns;
  }

  @Selector()
  static isLoading(state: SaleCampaignStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static getProducts(state: SaleCampaignStateModel): Product[] {
    return state.products;
  }

  @Selector()
  static getUpdatedCampaignId(state: SaleCampaignStateModel): string {
    return state.updatedCampaignId;
  }

  @Selector()
  static getUpdatedProductGroupId(state: SaleCampaignStateModel): string {
    return state.updatedProductGroupId;
  }

  @Selector()
  static getUpdatedCampaignOrderId(state: SaleCampaignStateModel): string {
    return state.updatedOrderId;
  }

  @Selector()
  static getCampaignOrders(state: SaleCampaignStateModel): ITransaction[] {
    return state.orders;
  }

  @Selector()
  static getSelectedProductGroup(state: SaleCampaignStateModel): IProductGroup {
    return state.selectedProductGroup;
  }

  @Selector()
  static getProductGroupList(state: SaleCampaignStateModel): IProductGroup[] {
    return state.productGroups;
  }

  private campaignsCache = new Map<string, Observable<ISaleCampaign[]>>();
  @Action(SaleCampaign.Actions.GetCampaigns)
  getCampaigns(
    { patchState, getState }: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.GetCampaigns
  ) {
    const type = action.type || '';
    const organizationId = action.organizationId || '';
    const cacheKey = `${organizationId}-${type}`;
    if (this.campaignsCache.has(cacheKey)) {
      return this.campaignsCache
        .get(cacheKey)!
        .pipe(
          tap((campaigns) =>
            this.patchCampaigns(patchState, getState, campaigns)
          )
        );
    }
    const campaignFunction = (
      action.type
        ? this.campaignService.campaignsByType(
            action.organizationId,
            action.type
          )
        : this.campaignService.byOrganization(action.organizationId)
    ) as Observable<ISaleCampaign[]>;

    this.campaignsCache.set(cacheKey, campaignFunction);

    return campaignFunction
      .pipe(takeUntil(this.unSubscribeAll$), debounceTime(3000))
      .subscribe((campaigns) => {
        const state = getState();
        const sortedCampaigns = ArrayUtilities.sort(
          campaigns,
          'endDate',
          SortOrder.desc
        );

        patchState({
          campaigns: [...sortedCampaigns],
          isLoading: false,
        });
      });
  }
  private patchCampaigns(
    patchState: (partial: Partial<SaleCampaignStateModel>) => void,
    getState: () => SaleCampaignStateModel,
    campaigns: ISaleCampaign[]
  ) {
    patchState({
      isLoading: false,
      campaigns: campaigns,
    });
  }
  @Action(SaleCampaign.Actions.GetCampaignOrders)
  getCampaignOrders(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.GetCampaignOrders
  ) {
    return this.campaignService
      .getCampaignOrders(action.campaignId, action.organizationId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((orders) => {
        const sortedOrders =
          orders && orders.length ? ArrayUtilities.sort(orders, 'title') : [];

        ctx.patchState({
          orders: [...sortedOrders],
          isLoading: false,
        });
      });
  }

  @Action(SaleCampaign.Actions.GetCampaign)
  getCampaign(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.GetCampaign
  ): Subscription {
    const state = ctx.getState();

    const campaignIndex = state.campaigns
      ? state.campaigns.findIndex((i) => i.id === action.campaignId)
      : -999;

    if (campaignIndex >= 0) {
      ctx.patchState({
        selectedCampaign: { ...state.campaigns[campaignIndex] },
        isLoading: false,
      });
      return of(state.campaigns[campaignIndex]).subscribe();
    }

    return this.campaignService
      .get(action.campaignId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign) => {
        ctx.patchState({
          selectedCampaign: campaign,
          isLoading: false,
        });
      });
  }

  @Action(SaleCampaign.Actions.InsertCampaign)
  insertCampaign(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.InsertCampaign
  ) {
    return this.campaignService
      .create(action.campaign)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign) => {
        const state = ctx.getState();
        const campaigns = state.campaigns;
        this.campaignsCache.clear();
        ctx.patchState({
          campaigns: [campaign, ...campaigns],
          isLoading: false,
          updatedCampaignId: campaign.id,
        });
      });
  }

  @Action(SaleCampaign.Actions.UpdateCampaign)
  updateCampaign(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.UpdateCampaign
  ) {
    return this.campaignService
      .update(action.campaign.id, action.campaign)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign) => {
        const state = ctx.getState();
        const campaigns = [...state.campaigns];
        const campaignIndex = campaigns.findIndex((d) => d.id === campaign.id);

        if (campaignIndex >= 0) {
          campaigns[campaignIndex] = campaign;
        }
        this.campaignsCache.clear();
        ctx.patchState({
          campaigns: [...campaigns],
          isLoading: false,
          updatedCampaignId: campaign.id,
        });
      });
  }

  @Action(SaleCampaign.Actions.DeleteCampaign)
  deleteCampaign(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.DeleteCampaign
  ) {
    const campaign = { ...action.campaign };
    campaign.status = 'Inactive';
    return this.campaignService
      .update(action.campaign.id, campaign)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign2) => {
        const state = ctx.getState();
        const campaigns = [...state.campaigns];
        const campaignIndex = campaigns.findIndex((d) => d.id === campaign.id);

        if (campaignIndex >= 0) {
          campaigns.splice(campaignIndex, 1);
        }
        this.campaignsCache.clear();
        ctx.patchState({
          campaigns: [...campaigns],
          updatedCampaignId: action.campaign.id,
          isLoading: false,
        });
      });
  }

  @Action(SaleCampaign.Actions.DeleteCampaignOrder)
  deleteCampaignOrder(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.DeleteCampaignOrder
  ) {
    const order = { ...action.order };
    order.status = TransactionStatusEnum.Deleted;

    return this.transactionService
      .deleteOrder(action.order.id, order)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign2) => {
        const state = ctx.getState();
        const orders = [...state.orders];
        const orderIndex = orders.findIndex((d) => d.id === order.id);

        if (orderIndex >= 0) {
          orders.splice(orderIndex, 1);
        }

        ctx.patchState({
          orders: [...orders],
          updatedOrderId: action.order.id,
          isLoading: false,
        });
      });
  }

  @Action(SaleCampaign.Actions.GetProductGroups)
  getProductGroups(
    { patchState }: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.GetProductGroups
  ) {
    return this.productGroupService
      .getProductGroups(action.organizationId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((productGroups) => {
        const sortedProductGroups = ArrayUtilities.sort(
          productGroups,
          'name',
          SortOrder.asc
        );

        if (sortedProductGroups) {
          patchState({
            productGroups: [...sortedProductGroups],
          });
        }

        patchState({ isLoading: false });
      });
  }

  @Action(SaleCampaign.Actions.GetProductGroup)
  getProductGroup(
    { getState, patchState }: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.GetProductGroup
  ) {
    const state = getState();
    const productGroups = state.productGroups;

    const selectedProductGroup = productGroups.find(
      (group) => group.id === action.productGroupId
    );

    patchState({
      selectedProductGroup: selectedProductGroup,
      isLoading: false,
    });
  }

  @Action(SaleCampaign.Actions.UpdateProductGroup)
  updateProductGroup(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.UpdateProductGroup
  ) {
    return this.productGroupService
      .update(action.productGroup.id, action.productGroup)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((productGroup) => {
        const state = ctx.getState();
        const productGroups = [...state.productGroups];
        const groupIndex = productGroups.findIndex(
          (d) => d.id === productGroup.id
        );

        if (groupIndex >= 0) {
          productGroups[groupIndex] = productGroup;
        }

        ctx.patchState({
          productGroups: [...productGroups],
          isLoading: false,
          updatedProductGroupId: productGroup.id,
        });
      });
  }

  @Action(SaleCampaign.Actions.InsertProductGroup)
  insertProductGroup(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.InsertProductGroup
  ) {
    return this.productGroupService
      .create(action.productGroup)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((productGroup) => {
        const state = ctx.getState();
        let productGroups = state.productGroups;
        if (!productGroups) productGroups = [];
        ctx.patchState({
          productGroups: [productGroup, ...productGroups],
          isLoading: false,
          updatedCampaignId: productGroup.id,
        });
      });
  }

  @Action(SaleCampaign.Actions.DeleteProductGroup)
  deleteProductGroup(
    ctx: StateContext<SaleCampaignStateModel>,
    action: SaleCampaign.Actions.DeleteProductGroup
  ) {
    const productGroup = { ...action.productGroup };
    productGroup.status = 'Inactive';
    return this.productGroupService
      .update(action.productGroup.id, productGroup)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((campaign2) => {
        const state = ctx.getState();
        const productGroups = [...state.productGroups];
        const groupIndex = productGroups.findIndex(
          (d) => d.id === productGroup.id
        );

        if (groupIndex >= 0) {
          productGroups.splice(groupIndex, 1);
        }

        ctx.patchState({
          productGroups: [...productGroups],
          updatedProductGroupId: action.productGroup.id,
          isLoading: false,
        });
      });
  }

  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }
}
